import type { ReactNode } from 'react';
import { cx } from '../primitives/cx.js';
import { passthrough } from '../primitives/passthrough.js';
import { Icon } from '../primitives/Icon.tsx';
import { Num } from '../primitives/Num.tsx';
import { SectionLabel } from '../primitives/Surface.tsx';

/**
 * ChartWidget - one chart, six shapes, every segment leading somewhere.
 *
 * The shape is a prop, not a fork. A funnel is a column chart whose bars are
 * ordered and whose gaps are the story; a donut is a bar chart wrapped round;
 * a KPI is a chart with one value. They differ in geometry and in nothing
 * else, and six components would be six places to forget the drill-through.
 *
 * What it will not do:
 *
 * **It does no arithmetic.** Every value arrives already formatted and every
 * magnitude arrives as a separate plain number. Nothing here parses a figure
 * back out of a formatted string - a currency symbol containing full stops
 * defeated that once already.
 *
 * **It will not draw a total it was not given a scope for.** When `scope` says
 * the figures cover records the reader cannot open, the widget says so, in
 * words, above the chart. A correct figure whose scope is invisible is the
 * defect that survives to production.
 *
 * **Every segment is a link when the caller supplies one, and plainly not a
 * link when it does not.** A dead segment that looked clickable would teach
 * the first person who tried that the product is broken.
 */

export type ChartShape = 'table' | 'bar' | 'column' | 'line' | 'donut' | 'funnel';

export interface ChartSegment {
  key: string;
  label: string;
  /** Already through the formatter. Never a number. */
  value: string;
  /** Geometry only. Never derived from `value`. */
  magnitude: number;
  /** Extra facts shown under the label: a count beside a total, say. */
  detail?: string[] | undefined;
  href?: string | undefined;
}

export interface ChartWidgetLabels {
  /** Names the chart for a screen reader. */
  chart: string;
  /** The invitation on each segment. */
  drillThrough: (segment: string) => string;
  /** Shown when the report returned no groups. */
  empty: string;
  /** Column headings when the shape is a table. */
  group: string;
  value: string;
  /** Announced when the figures cover more than the reader can open. */
  widerThanRows: (withheld: string) => string;
  /** Shown on a segment that leads nowhere, as its accessible description. */
  noDrillThrough: string;
  /**
   * What a funnel step's change is, relative to the step before it.
   *
   * A funnel's information is in the gaps, and a gap is meaningless without
   * saying what it is a gap from. This rendered as a bare `-11%` with nothing
   * naming the comparison, and a reader had to guess whether it was against
   * the previous step, the first step, or last month.
   *
   * Takes the ratio rather than a string, because this component holds no
   * format context by design - every value reaching it is already through the
   * formatter. The arithmetic belongs here, where the magnitudes are; the
   * rendering belongs to the caller, where the locale is.
   */
  stepChange: (ratio: number, previousStep: string) => string;
}

export interface ChartWidgetProps {
  shape: ChartShape;
  segments: ChartSegment[];
  labels: ChartWidgetLabels;
  /**
   * How far the figures reach, when that is further than the rows.
   *
   * Passed from the server's own answer rather than worked out here. The
   * widget is not entitled to an opinion about scope; it is obliged to show
   * the one it was given.
   */
  scope?: { withheld: string } | undefined;
  /** Rendered instead of segments. A failed widget says why in its own tile. */
  failure?: string | undefined;
  /** Shown above the chart: the question this answers. */
  caption?: ReactNode;
}

/**
 * Six shapes drawn from two primitives.
 *
 * 'column' and 'funnel' stack downward with horizontal bars; 'bar' and 'line'
 * run along the bottom; 'donut' is a proportional stack; 'table' is rows.
 * Funnel differs from column in one respect that matters: it shows the drop
 * from the previous step, because a funnel's information is in the gaps.
 */
const HORIZONTAL: readonly ChartShape[] = ['bar', 'funnel'];

export function ChartWidget({
  shape,
  segments,
  labels,
  scope,
  failure,
  caption,
  ...rest
}: ChartWidgetProps & Record<string, unknown>): JSX.Element {
  // Zero-safe: an empty report would otherwise divide by nothing and draw
  // every bar full height, which reads as a busy quarter.
  const tallest = segments.reduce((high, segment) => Math.max(high, Math.abs(segment.magnitude)), 0);
  const total = segments.reduce((sum, segment) => sum + Math.abs(segment.magnitude), 0);

  return (
    <section
      {...passthrough(rest)}
      data-component="ChartWidget"
      data-chart-shape={shape}
      aria-label={labels.chart}
      className="flex w-full min-w-0 flex-col gap-2"
    >
      {caption ? <p className="text-meta text-ink-meta">{caption}</p> : null}

      {scope ? (
        /*
         * The scope sentence, shown whenever the figures reach further than
         * the rows beneath them.
         *
         * Not an error and not a warning: a roll-up total covering work the
         * reader cannot open is correct and intended. What would be wrong is
         * leaving them to discover that the list is shorter than the number.
         */
        <p
          data-chart-scope
          className="flex items-start gap-2 rounded-card bg-sunken px-3 py-2 text-meta text-ink-meta"
        >
          <Icon name="Info" size="inline" decorative />
          {labels.widerThanRows(scope.withheld)}
        </p>
      ) : null}

      {failure !== undefined ? (
        <p role="alert" data-chart-failed className="rounded-card bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {failure}
        </p>
      ) : segments.length === 0 ? (
        <p data-chart-empty className="rounded-card bg-sunken px-3 py-2 text-dense text-ink-meta">
          {labels.empty}
        </p>
      ) : shape === 'table' ? (
        <TableShape segments={segments} labels={labels} />
      ) : shape === 'donut' ? (
        <DonutShape segments={segments} labels={labels} total={total} />
      ) : HORIZONTAL.includes(shape) ? (
        <StackedShape segments={segments} labels={labels} tallest={tallest} funnel={shape === 'funnel'} />
      ) : (
        <ColumnShape segments={segments} labels={labels} tallest={tallest} />
      )}
    </section>
  );
}

/** Wraps a segment in its link, or plainly does not. */
function Segment({
  segment,
  labels,
  className,
  children,
}: {
  segment: ChartSegment;
  labels: ChartWidgetLabels;
  className: string;
  children: ReactNode;
}): JSX.Element {
  if (segment.href === undefined) {
    return (
      <div data-chart-segment={segment.key} data-no-drill title={labels.noDrillThrough} className={className}>
        {children}
      </div>
    );
  }
  return (
    <a
      href={segment.href}
      data-chart-segment={segment.key}
      data-chart-link={segment.key}
      aria-label={labels.drillThrough(segment.label)}
      className={cx(
        className,
        'hover:bg-sunken focus-visible:outline-none',
      )}
    >
      {children}
    </a>
  );
}

function height(magnitude: number, tallest: number): number {
  return tallest === 0 ? 0 : Math.round((Math.abs(magnitude) / tallest) * 100);
}

function StackedShape({
  segments,
  labels,
  tallest,
  funnel,
}: {
  segments: ChartSegment[];
  labels: ChartWidgetLabels;
  tallest: number;
  funnel: boolean;
}): JSX.Element {
  return (
    <ol className="flex w-full flex-col gap-1">
      {segments.map((segment, index) => {
        const width = height(segment.magnitude, tallest);
        const previous = segments[index - 1];
        /*
         * A funnel's information is in the gaps, so each step carries its
         * change from the one before. A funnel that only showed the stage
         * counts would be a bar chart with a pointed name.
         *
         * Signed, and through the formatter. This computed a "drop" and
         * printed it as `-${drop}%` with the minus written by hand - so a step
         * BIGGER than the one before it, which is ordinary when a funnel is
         * ordered by value rather than by sequence, rendered as "--1131%".
         * Two minus signs, one of them typed into a template.
         */
        const change =
          funnel && previous && previous.magnitude > 0
            ? (segment.magnitude - previous.magnitude) / previous.magnitude
            : null;
        return (
          <li key={segment.key} className="w-full">
            <Segment segment={segment} labels={labels} className="flex w-full flex-col gap-0.5 rounded-card p-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className="truncate text-dense text-ink">{segment.label}</span>
                <Num data-chart-value={segment.key} className="text-dense text-ink">
                  {segment.value}
                </Num>
              </span>
              <span className="flex w-full items-center gap-2">
                <span className="h-2 w-full rounded-chip bg-sunken">
                  <span
                    data-chart-bar={segment.key}
                    data-magnitude={String(width)}
                    aria-hidden
                    className="block h-2 rounded-chip bg-bronze"
                    style={{ width: `${String(Math.max(width, 1))}%` }}
                  />
                </span>
                {change === null || previous === undefined ? null : (
                  <Num data-chart-drop={segment.key} className="shrink-0 text-meta text-ink-meta">
                    {labels.stepChange(change, previous.label)}
                  </Num>
                )}
              </span>
              {segment.detail?.length ? (
                <span className="truncate text-meta text-ink-meta">{segment.detail.join(' · ')}</span>
              ) : null}
            </Segment>
          </li>
        );
      })}
    </ol>
  );
}

function ColumnShape({
  segments,
  labels,
  tallest,
}: {
  segments: ChartSegment[];
  labels: ChartWidgetLabels;
  tallest: number;
}): JSX.Element {
  return (
    <ol className="flex w-full items-end gap-2 overflow-x-auto pb-1" data-scroll-container>
      {segments.map((segment) => {
        const tall = height(segment.magnitude, tallest);
        return (
          <li key={segment.key} className="flex h-plot min-w-chart-column flex-1 flex-col justify-end">
            <Segment segment={segment} labels={labels} className="flex h-full flex-col justify-end rounded-card">
              <span
                data-chart-bar={segment.key}
                data-magnitude={String(tall)}
                aria-hidden
                className="w-full rounded-control bg-bronze"
                style={{ height: `${String(Math.max(tall, 2))}%` }}
              />
              <span className="flex flex-col items-center gap-0.5 pt-1">
                <SectionLabel>{segment.label}</SectionLabel>
                <Num data-chart-value={segment.key} align="start" className="text-body text-ink">
                  {segment.value}
                </Num>
                {segment.detail?.map((line) => (
                  <Num key={line} align="start" className="text-meta text-ink-meta">
                    {line}
                  </Num>
                ))}
              </span>
            </Segment>
          </li>
        );
      })}
    </ol>
  );
}

function DonutShape({
  segments,
  labels,
  total,
}: {
  segments: ChartSegment[];
  labels: ChartWidgetLabels;
  total: number;
}): JSX.Element {
  return (
    <div className="flex w-full flex-col gap-2">
      {/* A proportional band rather than a ring: a ring needs SVG arcs, and a
          band answers the same question - what share is each - while staying
          legible at a dashboard tile's width. */}
      <span className="flex h-3 w-full overflow-hidden rounded-chip bg-sunken" aria-hidden>
        {segments.map((segment, index) => (
          <span
            key={segment.key}
            data-chart-slice={segment.key}
            className={cx('block h-3', SLICE_TONES[index % SLICE_TONES.length])}
            style={{ width: `${String(total === 0 ? 0 : (Math.abs(segment.magnitude) / total) * 100)}%` }}
          />
        ))}
      </span>
      <ol className="flex flex-col gap-0.5">
        {segments.map((segment, index) => (
          <li key={segment.key}>
            <Segment segment={segment} labels={labels} className="flex items-baseline gap-2 rounded-card px-1">
              {/* The swatch is the only thing that carries colour, and the
                  label beside it carries the meaning. Never colour alone. */}
              <span
                aria-hidden
                className={cx('inline-block h-2 w-2 rounded-chip', SLICE_TONES[index % SLICE_TONES.length])}
              />
              <span className="truncate text-dense text-ink">{segment.label}</span>
              <Num data-chart-value={segment.key} className="ms-auto text-dense text-ink">
                {segment.value}
              </Num>
            </Segment>
          </li>
        ))}
      </ol>
    </div>
  );
}

const SLICE_TONES = ['bg-bronze', 'bg-mark-info', 'bg-mark-success', 'bg-mark-warning', 'bg-ink-400'];

function TableShape({
  segments,
  labels,
}: {
  segments: ChartSegment[];
  labels: ChartWidgetLabels;
}): JSX.Element {
  return (
    // Every table sits inside a scroll container: a long group label pushes
    // this box sideways rather than the page.
    <div className="w-full overflow-x-auto" data-scroll-container>
      <table className="w-full min-w-panel-wide">
        <thead>
          <tr>
            <th scope="col" className="py-1 text-start text-micro uppercase tracking-label text-ink-label">
              {labels.group}
            </th>
            <th scope="col" className="py-1 text-end text-micro uppercase tracking-label text-ink-label">
              {labels.value}
            </th>
          </tr>
        </thead>
        <tbody>
          {segments.map((segment) => (
            <tr key={segment.key} className="border-t border-t-rule">
              <td className="py-1.5 text-body text-ink">
                <Segment segment={segment} labels={labels} className="block truncate rounded-card px-1">
                  {segment.label}
                </Segment>
              </td>
              <td className="py-1.5 text-end">
                <Num data-chart-value={segment.key} align="end" className="text-body text-ink">
                  {segment.value}
                </Num>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
